import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Check, Loader2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import { signaturesService, EmailSignature } from '@/services/signatures';
import { authService } from '@/services/auth';
import { toast } from 'react-hot-toast';
import { useCloudbridgeQuillImages } from '@/hooks/useCloudbridgeQuillImages';
import { cloudbridgeFolders } from '@/services/cloudbridgeUpload';
import ImageUploadOverlay from '@/components/email/ImageUploadOverlay';

interface ActiveSignature {
  signature_id: string;
  user_id: string;
  signature: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user: {
    username: string;
    first_name: string;
    last_name: string;
  };
}

const EmailSignatures: React.FC = () => {
  const navigate = useNavigate();
  const [signatures, setSignatures] = useState<EmailSignature[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newSignature, setNewSignature] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const {
    quillRef,
    quillModules: cloudbridgeModules,
    handleBodyChange,
    releaseTracked,
    discardTracked,
    isUploadingImage,
    uploadProgress,
    uploadFileName,
  } = useCloudbridgeQuillImages({ folder: cloudbridgeFolders.signature });

  // Quill modules configuration
  // const quillModules = {
  //   toolbar: [
  //     [{ 'header': [1, 2, false] }],
  //     ['bold', 'italic', 'underline', 'strike', 'blockquote'],
  //     [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
  //     ['link', 'image'],
  //     ['clean']
  //   ],
  //   clipboard: {
  //     matchVisual: false
  //   }
  // };

  // Custom CSS for ReactQuill styling
  const quillStyles = `
    .ql-container {
      border: 1px solid #e5e7eb !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      background-color: #fff !important;
    }
    .ql-editor {
      min-height: 100px !important;
      padding: 16px !important;
      outline: none !important;
      overflow-y: visible !important;
    }

    .ql-editor img {
      display: block;
      max-width: 100%;
      height: auto;
      margin: 10px 0;
    }

    .ql-editor:active {
      border: 1px solid #e5e7eb !important;
    }

    *:focus-visible {
      outline: 2px solid transparent;
      outline-offset: 2px;
      box-shadow: none !important;
      --tw-ring-offset-width: 2px;
      border: none !important;
    }

    .ql-toolbar {
      border: 1px solid #e5e7eb !important;
      background-color: #fff !important;
      padding: 8px 16px !important;
      border-radius: 8px 8px 0 0;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    .ql-toolbar button {
      border-radius: 4px !important;
      margin: 2px !important;
    }
    .ql-toolbar button:hover {
      background-color: #e5e7eb !important;
    }
    
    .ql-toolbar .ql-active {
      background-color: #dbeafe !important;
      color: #1e40af !important;
    }
    .ql-container.ql-snow {
      border-radius: 0 0 8px 8px;
    }
    .ql-toolbar.ql-snow {
      border: 1px solid #e5e7eb !important;
    }

    /* Image resize module styles */
    .ql-editor .image-resizer {
      border: 1px dashed #3b82f6;
      border-radius: 2px;
    }
    .ql-editor .image-resizer.resize-overlay {
      border: none;
      outline: 2px solid #3b82f6;
    }
    .ql-editor .image-resizer .resize-handle {
      display: block;
      width: 8px;
      height: 8px;
      background-color: #3b82f6;
      border-radius: 50%;
    }
    .ql-editor .image-resizer .resize-handle:hover {
      background-color: #2563eb;
    }
    .ql-editor .image-resizer .display-size {
      position: absolute;
      padding: 4px 8px;
      background-color: #1f2937;
      color: white;
      font-size: 12px;
      border-radius: 4px;
      pointer-events: none;
      user-select: none;
    }
  `;

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = quillStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [quillStyles]);

  // Register Quill modules
  ReactQuill.Quill.register('modules/imageResize', ImageResize);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'font': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: cloudbridgeModules.toolbar.handlers,
    },
    clipboard: cloudbridgeModules.clipboard,
    imageResize: {
      parchment: ReactQuill.Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize'],
      displayStyles: {
        backgroundColor: 'black',
        border: 'none',
        color: 'white'
      },
      handleStyles: {
        backgroundColor: 'black',
        border: 'none',
        color: 'white'
      }
    }
  }), [cloudbridgeModules]);

  // Quill formats - include all possible formats to ensure signature renders correctly
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image',
    'color', 'background',
    'align', 'direction', 'code-block', 'script',
    'size', 'header', 'font', 'code',
    'width', 'height', 'style'  // Add formats for image resizing
  ];

  useEffect(() => {
    loadSignatures();
  }, []);

  const loadSignatures = async () => {
    try {
      const data = await signaturesService.getAllSignatures();
      setSignatures(data);
    } catch (error: any) {
      const message = error.message || 'Failed to load signatures';
      toast.error(message);
      console.error('Error loading signatures:', error);
      
      // If unauthorized, redirect to login
      if (error.status === 401) {
        authService.logout();
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateActiveSignatureInLocalStorage = (signature: EmailSignature) => {
    const activeSignature: ActiveSignature = {
      signature_id: signature.signature_id,
      user_id: signature.user_id, // Convert to number since the schema expects it
      signature: signature.signature,
      is_active: signature.is_active,
      created_at: signature.created_at,
      updated_at: signature.updated_at,
      user: {
        username: authService.getCurrentUser()?.username || '',
        first_name: authService.getCurrentUser()?.first_name || '',
        last_name: authService.getCurrentUser()?.last_name || ''
      }
    };
    localStorage.setItem('activeSignature', JSON.stringify(activeSignature));
  };

  const handleSetActive = async (signatureId: string) => {
    try {
      // First, set all signatures to inactive in the UI (optimistic update)
      setSignatures(prev => prev.map(sig => ({
        ...sig,
        is_active: sig.signature_id === signatureId
      })));

      // Call the dedicated activation endpoint
      const updatedSignature = await signaturesService.activateSignature(signatureId);
      
      // Update the signatures list with the response from the server
      setSignatures(prev => prev.map(sig => 
        sig.signature_id === signatureId ? updatedSignature : { ...sig, is_active: false }
      ));

      // Update localStorage with the new active signature
      updateActiveSignatureInLocalStorage(updatedSignature);
      
      toast.success('Signature set as active');
    } catch (error: any) {
      const message = error.message || 'Failed to activate signature';
      toast.error(message);
      
      if (error.status === 401) {
        authService.logout();
        navigate('/login');
      } else {
        // Reload signatures to get the correct state
        loadSignatures();
      }
    }
  };

  const handleEdit = (signature: EmailSignature) => {
    setIsEditing(signature.signature_id);
    setEditText(signature.signature);
  };

  const handleSaveEdit = async (signatureId: string) => {
    try {
      setIsEditSaving(true);
      const signature = signatures.find(s => s.signature_id === signatureId);
      if (!signature) return;

      // Clean up the HTML before saving
      const cleanHtml = editText
        .replace(/<p><br\s*\/?><\/p>/g, '') // Remove empty paragraphs
        .replace(/(<br\s*\/?>){2,}/g, '<br>') // Replace multiple line breaks with single
        .replace(/^\s+|\s+$/g, '') // Remove leading/trailing whitespace
        .replace(/<p>\s+/g, '<p>') // Remove whitespace after paragraph start
        .replace(/\s+<\/p>/g, '</p>') // Remove whitespace before paragraph end
        .replace(/<p>(.*?)<\/p>/g, '$1<br>') // Convert paragraphs to line breaks
        .replace(/(<br>)+$/, ''); // Remove trailing line breaks

      const updatedSignature = await signaturesService.updateSignature(
        signatureId,
        cleanHtml,
        signature.is_active
      );

      setSignatures(prev => prev.map(sig =>
        sig.signature_id === signatureId ? updatedSignature : sig
      ));

      // If this is the active signature, update it in localStorage
      if (updatedSignature.is_active) {
        updateActiveSignatureInLocalStorage(updatedSignature);
      }

      releaseTracked();
      setIsEditing(null);
      toast.success('Signature updated successfully');
    } catch (error: any) {
      const message = error.message || 'Failed to update signature';
      toast.error(message);
      
      if (error.status === 401) {
        authService.logout();
        navigate('/login');
      }
    } finally {
      setIsEditSaving(false);
    }
  };

  const handleDelete = async (signatureId: string) => {
    try {
      // Check if this is the active signature
      const isActiveSignature = signatures.find(sig => 
        sig.signature_id === signatureId && sig.is_active
      );

      await signaturesService.deleteSignature(signatureId);
      setSignatures(prev => prev.filter(sig => sig.signature_id !== signatureId));

      // If we deleted the active signature, remove it from localStorage
      if (isActiveSignature) {
        localStorage.removeItem('activeSignature');
      }

      toast.success('Signature deleted successfully');
    } catch (error: any) {
      const message = error.message || 'Failed to delete signature';
      toast.error(message);
      
      if (error.status === 401) {
        authService.logout();
        navigate('/login');
      }
    }
  };

  const handleAddNew = async () => {
    if (newSignature.trim()) {
      try {
        setIsSaving(true);
        // Clean up the HTML before saving
        const cleanHtml = newSignature
          .replace(/<p><br\s*\/?><\/p>/g, '') // Remove empty paragraphs
          .replace(/(<br\s*\/?>){2,}/g, '<br>') // Replace multiple line breaks with single
          .replace(/^\s+|\s+$/g, '') // Remove leading/trailing whitespace
          .replace(/<p>\s+/g, '<p>') // Remove whitespace after paragraph start
          .replace(/\s+<\/p>/g, '</p>') // Remove whitespace before paragraph end
          .replace(/<p>(.*?)<\/p>/g, '$1<br>') // Convert paragraphs to line breaks
          .replace(/(<br>)+$/, ''); // Remove trailing line breaks

        const createdSignature = await signaturesService.createSignature(cleanHtml);
        setSignatures(prev => [...prev, createdSignature]);

        // If this is the first signature, make it active
        if (signatures.length === 0) {
          await handleSetActive(createdSignature.signature_id);
        }

        releaseTracked();
        setNewSignature('');
        setIsAddingNew(false);
        toast.success('Signature created successfully');
      } catch (error: any) {
        const message = error.message || 'Failed to create signature';
        toast.error(message);
        
        if (error.status === 401) {
          authService.logout();
          navigate('/login');
        }
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        padding: '24px',
        textAlign: 'center',
        color: '#6b7280'
      }}>
        Loading signatures...
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: '#ffffff', 
      borderRadius: '8px', 
      border: '1px solid #e5e7eb', 
      padding: '16px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{ 
          fontSize: '18px',
          fontWeight: '600',
          color: '#111827',
          margin: 0
        }}>
          Email Signatures
        </h3>
        <button
          onClick={() => setIsAddingNew(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2563eb'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#3b82f6'}
        >
          <Plus size={16} />
          Add
        </button>
      </div>

      {isAddingNew && (
        <div style={{
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ marginBottom: '12px', position: 'relative' }}>
            <ImageUploadOverlay
              active={isUploadingImage}
              progress={uploadProgress}
              fileName={uploadFileName}
            />
            <ReactQuill
              ref={quillRef}
              value={newSignature}
              onChange={(value) => handleBodyChange(value, setNewSignature)}
              modules={quillModules}
              formats={formats}
              style={{
                marginBottom: '0px'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                discardTracked();
                setIsAddingNew(false);
                setNewSignature('');
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAddNew}
              disabled={isSaving}
              style={{
                padding: '6px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isSaving ? 0.7 : 1
              }}
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {signatures.map(signature => (
          <div
            key={signature.signature_id}
            style={{
              padding: '16px',
              backgroundColor: signature.is_active ? '#f0f9ff' : '#f9fafb',
              borderRadius: '8px',
              border: `1px solid ${signature.is_active ? '#bae6fd' : '#e5e7eb'}`,
              transition: 'all 0.2s ease'
            }}
          >
            {isEditing === signature.signature_id ? (
              <div style={{ position: 'relative' }}>
                <ImageUploadOverlay
                  active={isUploadingImage}
                  progress={uploadProgress}
                  fileName={uploadFileName}
                />
                <ReactQuill
                  ref={quillRef}
                  value={editText}
                  onChange={(value) => handleBodyChange(value, setEditText)}
                  modules={quillModules}
                  formats={formats}
                  style={{
                    marginBottom: '0px'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      discardTracked();
                      setIsEditing(null);
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#374151'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveEdit(signature.signature_id)}
                    disabled={isEditSaving}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isEditSaving ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: isEditSaving ? 0.7 : 1
                    }}
                  >
                    {isEditSaving ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Saving...
                      </>
                    ) : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {signature.is_active && (
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: '#0ea5e9',
                        color: 'white',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Active
                      </span>
                    )}
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      Last updated: {new Date(signature.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {!signature.is_active && (
                      <button
                        onClick={() => handleSetActive(signature.signature_id)}
                        style={{
                          padding: '6px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Set as active"
                      >
                        <Check size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(signature)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(signature.signature_id)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div 
                  style={{
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}
                  dangerouslySetInnerHTML={{ __html: signature.signature }}
                />
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmailSignatures; 